import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { Search, RefreshCw, Download, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface RoomPackageOrder {
  id: number;
  user_id: number;
  username: string;
  amount: string;
  period: string;
  status: string;
  txid: string | null;
  sender_address: string | null;
  created_at: string;
  completed_at: string | null;
  order_type?: string;
}

interface RoomMembershipOrder {
  id: number;
  user_id: number;
  username: string;
  room_id: number;
  room_name: string;
  amount: string;
  price_type: string;
  status: string;
  txid: string | null;
  system_note: string | null;
  created_at: string;
  order_type?: string;
}

interface RoomCreationOrder {
  id: number;
  user_id: number;
  username: string;
  room_name: string;
  room_description?: string;
  amount: string;
  status: string;
  txid: string | null;
  sender_address: string | null;
  system_note: string | null;
  created_at: string;
  order_type?: string;
}

type Order = RoomPackageOrder | RoomMembershipOrder | RoomCreationOrder;

const AdminRoomMembershipOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    username: '',
    status: 'completed'
  });
  const [activeTab, setActiveTab] = useState<'all' | 'package' | 'membership' | 'creation'>('all');
  const pageSize = 20;

  useEffect(() => {
    loadOrders();
  }, [page, filters.username, filters.status, activeTab]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        status: filters.status
      });

      if (filters.username) params.append('username', filters.username);

      const fetchPackageOrders = activeTab === 'all' || activeTab === 'package';
      const fetchMembershipOrders = activeTab === 'all' || activeTab === 'membership';
      const fetchCreationOrders = activeTab === 'all' || activeTab === 'creation';

      const promises: Promise<{ type: 'package' | 'membership' | 'creation'; data: { success: boolean; orders: any[]; pagination: { page: number; pageSize: number; total: number } } }>[] = [];
      if (fetchPackageOrders) {
        promises.push(api.get<{ success: boolean; orders: RoomPackageOrder[]; pagination: { page: number; pageSize: number; total: number } }>(`/payment/admin/room-package-orders?${params}`).then(r => ({ type: 'package' as const, data: r })).catch(err => {
          console.error('加载房间套餐订单失败:', err);
          return { type: 'package' as const, data: { success: false, orders: [], pagination: { page: 1, pageSize: 20, total: 0 } } };
        }));
      }
      if (fetchMembershipOrders) {
        promises.push(api.get<{ success: boolean; orders: RoomMembershipOrder[]; pagination: { page: number; pageSize: number; total: number } }>(`/payment/admin/room-membership-orders?${params}`).then(r => ({ type: 'membership' as const, data: r })).catch(err => {
          console.error('加载房间会员订单失败:', err);
          return { type: 'membership' as const, data: { success: false, orders: [], pagination: { page: 1, pageSize: 20, total: 0 } } };
        }));
      }
      if (fetchCreationOrders) {
        promises.push(api.get<{ success: boolean; orders: RoomCreationOrder[]; pagination: { page: number; pageSize: number; total: number } }>(`/payment/admin/room-creation-orders?${params}`).then(r => ({ type: 'creation' as const, data: r })).catch(err => {
          console.error('加载房间创建订单失败:', err);
          return { type: 'creation' as const, data: { success: false, orders: [], pagination: { page: 1, pageSize: 20, total: 0 } } };
        }));
      }

      const results = await Promise.all(promises);
      
      let allOrders: Order[] = [];
      let totalCount = 0;

      results.forEach(result => {
        if (result.data.success && result.data.orders) {
          const mappedOrders = result.data.orders.map(o => ({ ...o, order_type: result.type }));
          allOrders = [...allOrders, ...mappedOrders];
          totalCount += result.data.pagination?.total || 0;
        }
      });

      allOrders.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setOrders(allOrders);
      setTotal(totalCount);
    } catch (error: any) {
      console.error('加载订单失败:', error);
      toast.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadOrders();
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; label: string }> = {
    pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '待支付' },
    awaiting_confirmation: { bg: 'bg-blue-100', text: 'text-blue-800', label: '待确认' },
    success: { bg: 'bg-green-100', text: 'text-green-800', label: '已完成' },
    completed: { bg: 'bg-green-100', text: 'text-green-800', label: '已完成' },
    failed: { bg: 'bg-red-100', text: 'text-red-800', label: '失败' },
    expired: { bg: 'bg-gray-100', text: 'text-gray-800', label: '已过期' },
    cancelled: { bg: 'bg-gray-100', text: 'text-gray-600', label: '已取消' },
    refunded: { bg: 'bg-orange-100', text: 'text-orange-600', label: '已退款' }
  };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const formatAddress = (address: string | null) => {
    if (!address) return '-';
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  const formatTime = (time: string | null) => {
    if (!time) return '-';
    return new Date(time).toLocaleString('zh-CN');
  };

  const getPeriodLabel = (order: Order) => {
    if ('period' in order) {
      return order.period === 'monthly' ? '月付' : order.period === 'quarterly' ? '季付' : '年费';
    }
    if ('price_type' in order) {
      return order.price_type === 'monthly' ? '月付' : order.price_type === 'yearly' ? '年费' : order.price_type === 'permanent' ? '永久' : '免费';
    }
    if ('room_description' in order) {
      return '创建房间';
    }
    return '-';
  };

  const getOrderTypeLabel = (order_type?: string) => {
    return order_type === 'package' ? '房主开通' : order_type === 'membership' ? '会员购买' : order_type === 'creation' ? '房间创建' : '-';
  };

  const exportOrders = () => {
    const csv = [
      ['ID', '订单类型', '用户名', '房间', '金额', '套餐周期', 'TXID', '状态', '备注', '创建时间', '完成时间'].join(','),
      ...orders.map(order => [
        order.id,
        getOrderTypeLabel(order.order_type),
        order.username,
        'room_name' in order ? order.room_name || '-' : '-',
        order.amount,
        getPeriodLabel(order),
        ('txid' in order && order.txid) || '-',
        order.status,
        'system_note' in order ? (order.system_note || '-') : '-',
        formatTime(order.created_at),
        'completed_at' in order ? formatTime(order.completed_at) : '-'
      ].join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `room_orders_${Date.now()}.csv`;
    link.click();
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">房间订单管理</h1>
        <button
          onClick={exportOrders}
          className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
        >
          <Download className="w-4 h-4" />
          导出流水
        </button>
      </div>

      {/* 选项卡 */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'all', label: '全部订单' },
            { key: 'package', label: '房主开通' },
            { key: 'membership', label: '会员购买' },
            { key: 'creation', label: '房间创建' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key as any);
                setPage(1);
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-pink-500 text-pink-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* 筛选表单 */}
      <form onSubmit={handleSearch} className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">用户名</label>
            <input
              type="text"
              value={filters.username}
              onChange={(e) => setFilters({ ...filters, username: e.target.value })}
              placeholder="搜索用户名"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">状态</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
            >
              <option value="completed">已完成</option>
              <option value="success">已成功</option>
              <option value="all">全部</option>
              <option value="pending">待支付</option>
              <option value="awaiting_confirmation">待确认</option>
              <option value="failed">失败</option>
              <option value="expired">已过期</option>
              <option value="cancelled">已取消</option>
              <option value="refunded">已退款</option>
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
            >
              <Search className="w-4 h-4" />
              搜索
            </button>
            <button
              type="button"
              onClick={() => {
                setFilters({ username: '', status: 'completed' });
                setPage(1);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              重置
            </button>
          </div>
        </div>
      </form>

      {/* 数据表格 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">ID</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">订单类型</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">用户名</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">房间</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">金额</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">套餐周期</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">TXID</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">状态</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">备注</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">创建时间</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">完成时间</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} className="text-center py-12">
                    <RefreshCw className="w-8 h-8 mx-auto animate-spin text-pink-500" />
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center py-12 text-gray-500">暂无数据</td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={`${order.order_type}-${order.id}`} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm">{order.id}</td>
                    <td className="py-3 px-4 text-sm">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        order.order_type === 'package' ? 'bg-blue-100 text-blue-800' :
                        order.order_type === 'membership' ? 'bg-green-100 text-green-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {getOrderTypeLabel(order.order_type)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm font-medium">{order.username}</td>
                    <td className="py-3 px-4 text-sm">
                      {'room_name' in order ? order.room_name || '-' : '-'}
                    </td>
                    <td className="py-3 px-4 text-sm font-bold text-green-600">{order.amount}</td>
                    <td className="py-3 px-4 text-sm">{getPeriodLabel(order)}</td>
                    <td className="py-3 px-4 text-sm font-mono text-xs">
                      {'txid' in order && order.txid ? (
                        <a
                          href={`https://tronscan.org/#/transaction/${order.txid}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-pink-600 hover:underline"
                        >
                          {formatAddress(order.txid)}
                        </a>
                      ) : '-'}
                    </td>
                    <td className="py-3 px-4">{getStatusBadge(order.status)}</td>
                    <td className="py-3 px-4 text-sm">
                      {'system_note' in order ? order.system_note || '-' : '-'}
                    </td>
                    <td className="py-3 px-4 text-sm">{formatTime(order.created_at)}</td>
                    <td className="py-3 px-4 text-sm">
                      {'completed_at' in order ? formatTime(order.completed_at) : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {total > pageSize && (
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t">
            <div className="text-sm text-gray-600">
              共 {total} 条记录，第 {page} / {Math.ceil(total / pageSize)} 页
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 bg-white border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-100"
              >
                上一页
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= Math.ceil(total / pageSize)}
                className="px-3 py-1 bg-white border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-100"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRoomMembershipOrders;
